// A note's revisions, oldest first (architecture sprint-12 §9.2). Admin only; deleted notes have history too.
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useNoteHistory } from '@/hooks/use-notes';
import { formatDateTime, formatLocalDate } from '@/lib/format';
import type { NoteRevision, NoteRevisionSide } from '@/types/notes';

const ACTION_LABEL: Record<NoteRevision['action'], string> = { edited: 'Edited', deleted: 'Deleted' };

function Side({ label, side }: { label: string; side: NoteRevisionSide }): JSX.Element {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground">
        {formatLocalDate(side.date)} · {side.subjectName}
      </p>
      <p className="whitespace-pre-wrap text-sm text-foreground">{side.description}</p>
    </div>
  );
}

export function NoteHistoryDialog({ projectId, noteId, onClose }: { projectId: string; noteId: string | null; onClose: () => void }): JSX.Element {
  const history = useNoteHistory(projectId, noteId);
  return (
    <Dialog open={noteId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Note history</DialogTitle>
          <DialogDescription>Every edit and delete, oldest first.</DialogDescription>
        </DialogHeader>
        {!history.data ? (
          <Skeleton className="h-24 w-full" />
        ) : history.data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">This note hasn&apos;t been changed.</p>
        ) : (
          <ol className="space-y-3">
            {history.data.items.map((r) => (
              <li key={r.id} className="card space-y-2">
                <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant={r.action === 'deleted' ? 'danger' : 'outline'}>{ACTION_LABEL[r.action]}</Badge>
                  {r.actor.name} · {formatDateTime(r.at)}
                </p>
                <Side label="Before" side={r.before} />
                {r.after && <Side label="After" side={r.after} />}
              </li>
            ))}
          </ol>
        )}
        <DialogFooter>
          <Button variant="ghost" className="flex-1" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
